import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Paper,
  Grid,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  People,
  TrendingUp,
  Description,
  Notifications,
  Person,
  Logout,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const notifications = [
    { text: 'New lead assigned to you', time: '5 min ago' },
    { text: 'Opportunity closed won!', time: '1 hour ago' },
    { text: 'Contract renewal reminder', time: '2 hours ago' },
  ];

  const quickActions = [
    {
      title: 'Manage Leads',
      description: 'View and manage your leads',
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#667eea',
      path: '/leads',
    },
    {
      title: 'Opportunities',
      description: 'Track your opportunities',
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#764ba2',
      path: '/opportunities',
    },
    {
      title: 'Contracts',
      description: 'Review your contracts',
      icon: <Description sx={{ fontSize: 40 }} />,
      color: '#f59e0b',
      path: '/contracts',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#fafbfc' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
          color: 'white',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Welcome back, {user?.firstName || 'Partner'}!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage your partnership activities and track your progress
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/leads')}
                sx={{
                  background: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                  },
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                New Lead
              </Button>

              {/* Notifications */}
              <IconButton
                onClick={handleNotificationOpen}
                sx={{ color: 'white' }}
              >
                <Badge badgeContent={3} color="error">
                  <Notifications />
                </Badge>
              </IconButton>

              {/* User Profile */}
              <IconButton onClick={handleProfileMenuOpen} size="small">
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    width: 40,
                    height: 40,
                  }}
                >
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            maxHeight: 400,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6">Notifications</Typography>
        </Box>
        <Divider />
        {notifications.map((notification, index) => (
          <MenuItem key={index} onClick={handleNotificationClose}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2">{notification.text}</Typography>
              <Typography variant="caption" color="text.secondary">
                {notification.time}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleNotificationClose}>
          <Typography variant="body2" color="primary" sx={{ width: '100%', textAlign: 'center' }}>
            View All Notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight="bold">
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>

      <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
        {/* Quick Actions */}
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                sx={{
                  p: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  },
                }}
                onClick={() => navigate(action.path)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    color: action.color,
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Getting Started */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Getting Started
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            As a partner, you have access to manage leads, opportunities, and contracts. 
            Use the navigation menu to explore different sections of the portal.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/leads')}>
              View Leads
            </Button>
            <Button variant="outlined" onClick={() => navigate('/profile')}>
              Update Profile
            </Button>
          </Stack>
        </Paper>

        {/* Resources */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Partner Resources
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Documentation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access partner documentation and guides to help you succeed.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get help from our partner support team when you need it.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default DashboardPage;